import { useEffect, useRef, useState } from "react";
import {
  Button,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Select,
  Text,
  Textarea,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { SendRegular, SettingsRegular } from "@fluentui/react-icons";
import ChatMessage from "./ChatMessage";

const useStyles = makeStyles({
  wrapper: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minHeight: 0,
    gap: "10px",
  },
  history: {
    display: "flex",
    flexDirection: "column",
    gap: "14px",
    overflowY: "auto",
    flex: 1,
    minHeight: 0,
    padding: "4px 2px 8px 2px",
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    color: tokens.colorNeutralForeground3,
    fontStyle: "italic",
    padding: "20px 8px",
    textAlign: "center",
  },
  inputBar: {
    display: "flex",
    alignItems: "flex-end",
    gap: "6px",
    padding: "6px",
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: "22px",
    backgroundColor: tokens.colorNeutralBackground1,
  },
  textarea: {
    flex: 1,
    border: "none",
    backgroundColor: "transparent",
    "::after": { display: "none" },
    "::before": { display: "none" },
    "& textarea": {
      boxSizing: "border-box",
      height: "32px",
      minHeight: "32px",
      lineHeight: "20px",
      paddingTop: "6px",
      paddingBottom: "6px",
      overflowY: "hidden",
    },
  },
  iconButton: {
    backgroundColor: "var(--aco-accent)",
    color: "#fff",
    border: "none",
    width: "32px",
    minWidth: "32px",
    height: "32px",
    borderRadius: "50%",
    padding: 0,
    flexShrink: 0,
    ":hover": { backgroundColor: "var(--aco-accent-hover)", color: "#fff" },
    ":active": { backgroundColor: "var(--aco-accent-active)", color: "#fff" },
    ":disabled": {
      backgroundColor: tokens.colorNeutralBackgroundDisabled,
      color: tokens.colorNeutralForegroundDisabled,
    },
  },
  settingsButton: {
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground2,
    border: "none",
    width: "32px",
    minWidth: "32px",
    height: "32px",
    borderRadius: "50%",
    padding: 0,
    flexShrink: 0,
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground3Hover,
      color: tokens.colorNeutralForeground1,
    },
  },
});

export default function ChatPanel({
  messages,
  isStreaming,
  canSend,
  onSend,
  onUseAsMail,
  replyLang,
  setReplyLang,
  tone,
  setTone,
  length,
  setLength,
  t,
}) {
  const styles = useStyles();
  const [draft, setDraft] = useState("");
  const historyRef = useRef(null);
  const textareaRef = useRef(null);

  const MAX_LINES = 5;
  const LINE_HEIGHT = 20;
  const PADDING_V = 12; // 6px top + 6px bottom
  const MIN_HEIGHT = LINE_HEIGHT + PADDING_V; // 32px matches send button
  const MAX_HEIGHT = LINE_HEIGHT * MAX_LINES + PADDING_V; // 112px 5 lines

  useEffect(() => {
    const node = historyRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [messages, isStreaming]);

  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    if (draft.length === 0) {
      // Keep one line at rest; don't let a long placeholder inflate the height.
      ta.style.height = `${MIN_HEIGHT}px`;
      ta.style.overflowY = "hidden";
      return;
    }
    ta.style.height = "auto";
    const next = Math.max(MIN_HEIGHT, Math.min(ta.scrollHeight, MAX_HEIGHT));
    ta.style.height = `${next}px`;
    ta.style.overflowY = ta.scrollHeight > MAX_HEIGHT ? "auto" : "hidden";
  }, [draft]);

  const isEmpty = messages.length === 0;
  const isFirstMessage = isEmpty;
  const trimmed = draft.trim();
  const sendDisabled =
    isStreaming || !canSend || (!isFirstMessage && trimmed.length === 0);

  function handleSend() {
    if (sendDisabled) return;
    onSend(trimmed);
    setDraft("");
  }

  function handleKey(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  return (
    <div className={styles.wrapper}>
      <div ref={historyRef} className={styles.history}>
        {isEmpty && (
          <div className={styles.emptyState}>
            <Text size={300} weight="semibold" style={{ color: "var(--aco-accent)" }}>
              {t("chat.empty_title")}
            </Text>
            <Text size={200}>{t("chat.empty_hint")}</Text>
          </div>
        )}

        {messages.map((msg, idx) => {
          const isLatestAssistant =
            msg.role === "assistant" && idx === messages.length - 1;
          const streamingThis = isLatestAssistant && isStreaming;
          return (
            <ChatMessage
              key={msg.id ?? idx}
              message={msg}
              isStreaming={streamingThis}
              onUseAsMail={() => onUseAsMail(msg.content)}
              t={t}
            />
          );
        })}
      </div>

      <div className={styles.inputBar}>
        <Textarea
          className={styles.textarea}
          textarea={{ ref: textareaRef }}
          value={draft}
          onChange={(_, d) => setDraft(d.value)}
          onKeyDown={handleKey}
          placeholder={t("chat.placeholder")}
          resize="none"
          rows={1}
          appearance="filled-lighter"
          disabled={!canSend}
        />
        <Button
          icon={<SendRegular />}
          className={styles.iconButton}
          onClick={handleSend}
          disabled={sendDisabled}
          aria-label={t("chat.send")}
        />
        <Popover trapFocus positioning="above-end">
          <PopoverTrigger disableButtonEnhancement>
            <Button
              icon={<SettingsRegular />}
              className={styles.settingsButton}
              disabled={isStreaming}
              aria-label={t("settings.reply")}
            />
          </PopoverTrigger>
          <PopoverSurface
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "12px",
              padding: "14px",
              minWidth: "220px",
            }}
          >
            <Text weight="bold" style={{ color: "var(--aco-accent)" }}>
              {t("settings.reply")}
            </Text>

            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "12px",
                  color: tokens.colorNeutralForeground2,
                  marginBottom: "4px",
                }}
              >
                {t("settings.reply_lang")}
              </label>
              <Select
                value={replyLang}
                onChange={(_, d) => setReplyLang(d.value)}
                style={{ width: "100%" }}
              >
                <option value="fr">Français</option>
                <option value="en">English</option>
                <option value="de">Deutsch</option>
              </Select>
            </div>

            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "12px",
                  color: tokens.colorNeutralForeground2,
                  marginBottom: "4px",
                }}
              >
                {t("options.tone")}
              </label>
              <Select
                value={tone}
                onChange={(_, d) => setTone(d.value)}
                style={{ width: "100%" }}
              >
                <option value="professional">{t("tone.professional")}</option>
                <option value="professional_friendly">{t("tone.friendly")}</option>
                <option value="direct">{t("tone.direct")}</option>
              </Select>
            </div>

            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "12px",
                  color: tokens.colorNeutralForeground2,
                  marginBottom: "4px",
                }}
              >
                {t("options.length")}
              </label>
              <Select
                value={length}
                onChange={(_, d) => setLength(d.value)}
                style={{ width: "100%" }}
              >
                <option value="short">{t("length.short")}</option>
                <option value="medium">{t("length.medium")}</option>
                <option value="long">{t("length.long")}</option>
                <option value="full">{t("length.full")}</option>
              </Select>
            </div>
          </PopoverSurface>
        </Popover>
      </div>
    </div>
  );
}
